import { atom } from 'jotai';
import { RESET, atomWithReset, atomWithStorage } from 'jotai/utils';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

import { BPAScanListData, BPAScanPaginationConfig } from './types';

type FilterValues = Record<string, unknown>;

export const DATE_FILTER_KEYS = [
  't12_created_at',
  't12_updated_at',
  'started_at',
  'finished_at',
] as const;
type DateFilterKey = (typeof DATE_FILTER_KEYS)[number];

export const selectedRowIdsAtom = atomWithReset<number[]>([]);
/** Bare string token — writing a new value triggers a re-fetch without any stored state. */
export const bpaScansGridDataRefreshAtom = atom<string>('');

/**
 * Guards against fetching before filter defaults have been applied.
 * Set to `true` by `useFilterConfig` once initial filter values are resolved.
 * NOTE: If the Filters panel is ever conditionally unmounted, the grid will
 * stall silently until this atom is set back to `true`.
 */
export const isBpaQuerystringFiltersInitializedAtom = atom<boolean>(false);

export const DEFAULT_BPA_SCAN_COLUMN_KEYS = [
  'account_id',
  'integration_label',
  'region',
  'benchmark_description',
  'status',
  'job_type',
  'started_at',
  'finished_at',
  'scan_duration',
  'actions',
];

export const columnKeysAtom = atomWithStorage<string[]>(
  'bpa_scans_columns',
  DEFAULT_BPA_SCAN_COLUMN_KEYS,
);

export const filterValuesAtom = atomWithReset<FilterValues>({});

export const parsedFilterValuesAtom = atom<FilterValues>((get) => {
  const filterValues = get(filterValuesAtom);
  const parsed: FilterValues = { ...filterValues };

  DATE_FILTER_KEYS.forEach((key: DateFilterKey) => {
    const val = parsed[key];
    if (val && Array.isArray(val)) {
      parsed[key] = val.map(
        (range: { from: string | null; to: string | null }) => ({
          from: range.from ? new Date(range.from) : null,
          to: range.to ? new Date(range.to) : null,
        }),
      );
    }
  });

  return parsed;
});

export const paginationConfigAtom = atom<BPAScanPaginationConfig>({
  page_size: DEFAULT_PAGE_SIZE,
  page: 1,
});

export const orderbyAtom = atomWithReset<string>('-started_at');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('desc');

export const bpaScansDataQueryParamsAtom = atom((get) => {
  const companyId = get(companyIdAtom);

  // Fail fast — no company selected yet
  if (!companyId || companyId === -1) return undefined;

  const paginationConfig = get(paginationConfigAtom);
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  const filterValues = get(parsedFilterValuesAtom);
  // Subscribe to the refresh token so manual refresh triggers a re-fetch
  get(bpaScansGridDataRefreshAtom);

  const sortPrefix = order === 'desc' ? '-' : '';
  const cleanOrderBy = orderBy.replace(/^-/, '');
  const ordering = `${sortPrefix}${cleanOrderBy}`;

  const params: Record<string, string> = {
    company_id: String(companyId),
    page: String(paginationConfig.page),
    page_size: String(paginationConfig.page_size),
    ordering,
  };

  Object.entries(filterValues).forEach(([key, value]) => {
    // Date range filters
    if ((DATE_FILTER_KEYS as readonly string[]).includes(key)) {
      if (Array.isArray(value) && value.length > 0) {
        const range = value[0] as { from: Date | null; to: Date | null };
        if (range?.from instanceof Date) {
          params[`${key}_after`] = range.from.toISOString();
        }
        if (range?.to instanceof Date) {
          params[`${key}_before`] = range.to.toISOString();
        }
      }
      return;
    }

    // Search — SearchInputFilterConfig stores the value as a single-element array.
    // Unwrap it before applying so typeof checks work correctly.
    if (key === 'search') {
      const searchVal = Array.isArray(value) ? value[0] : value;
      if (typeof searchVal === 'string' && searchVal.trim() !== '') {
        params['search'] = searchVal.trim();
      }
      return;
    }

    // General filters — strip empty-string sentinels used by MultiSelect 'All' options
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        const nonEmpty = value.filter((v) => v !== '' && v !== null);
        if (nonEmpty.length > 0) params[key] = nonEmpty.join(',');
      } else if (value !== '') {
        params[key] = String(value);
      }
    }
  });

  return params;
});

export const bpaScansDataAtom = atom<BPAScanListData>({
  results: [],
  count: 0,
  loading: false,
});

/**
 * Atomically applies new filter values and resets pagination, sort order, and
 * row selection in a single jotai write transaction — guaranteeing a single
 * recompute of bpaScansDataQueryParamsAtom and therefore a single API fetch.
 */
export const applyFiltersAtom = atom(
  null,
  (_get, set, newFilterValues: FilterValues) => {
    set(filterValuesAtom, newFilterValues);
    set(selectedRowIdsAtom, RESET);
    set(orderbyAtom, RESET);
    set(sortOrderAtom, RESET);
    set(paginationConfigAtom, { page_size: DEFAULT_PAGE_SIZE, page: 1 });
  },
);
